import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, X, Plus, Trash2, BadgeCheck, AlertTriangle, ArrowUp, ArrowDown, Instagram } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { DICT, SERVICE_KEYS } from "@/site/i18n";

const B = process.env.REACT_APP_BACKEND_URL;
const lbl = "text-[11px] font-bold uppercase tracking-wider text-slate-600";

export const BiField = ({ label, value = {}, onChange, area, testid }) => (
  <div className="grid gap-1.5">
    <div className={lbl}>{label}</div>
    <div className="grid gap-2 md:grid-cols-2">
      {["it", "en"].map((l) => {
        const C = area ? Textarea : Input;
        return (
          <div key={l} className="relative">
            <span className="absolute right-2 top-2 z-10 rounded bg-[#0A1128] px-1.5 text-[10px] font-bold uppercase text-white">{l}</span>
            <C value={value[l] || ""} onChange={(e) => onChange({ ...value, [l]: e.target.value })} rows={area ? 4 : undefined}
              className="bg-white rounded-xl pr-10" data-testid={`${testid}-${l}`} />
          </div>
        );
      })}
    </div>
  </div>
);

export const ImagePick = ({ label, url, onChange, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/sito/media", fd);
      onChange(r.data.key, r.data.url);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="grid gap-1.5">
      <div className={lbl}>{label}</div>
      <div className="flex items-center gap-3">
        <div className="h-20 w-28 rounded-xl border border-[#5FB3EA] bg-white overflow-hidden flex items-center justify-center text-[11px] text-slate-400">
          {url ? <img src={`${B}${url}`} alt="" className="h-full w-full object-cover" /> : "Segnaposto"}
        </div>
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid={`${testid}-input`} />
        <Button type="button" variant="outline" onClick={() => ref.current?.click()} disabled={busy} className="rounded-full font-bold" data-testid={`${testid}-upload`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}Carica
        </Button>
        {url && <Button type="button" variant="ghost" onClick={() => onChange(null, null)} className="rounded-full" aria-label="Rimuovi" data-testid={`${testid}-remove`}><X className="h-4 w-4" /></Button>}
      </div>
    </div>
  );
};

export const ServiceTags = ({ value, onChange }) => (
  <div className="grid gap-1.5">
    <div className={lbl}>Servizi</div>
    <div className="flex flex-wrap gap-2">
      {SERVICE_KEYS.map((k) => {
        const on = value.includes(k);
        return <button type="button" key={k} onClick={() => onChange(on ? value.filter((x) => x !== k) : [...value, k])} data-testid={`case-edit-servizio-${k}`}
          className={`min-h-[36px] rounded-full px-3 text-xs font-bold border transition-colors ${on ? "bg-[#0A1128] text-white border-[#0A1128]" : "bg-white border-[#5FB3EA] text-[#0A1128]"}`}>{DICT.it.servizi.items[k][0]}</button>;
      })}
    </div>
  </div>
);

const EMPTY_KPI = { label: { it: "", en: "" }, valore: "—", periodo: { it: "", en: "" }, confermato: false };

export const KpiEditor = ({ value, onChange }) => {
  const set = (i, patch) => onChange(value.map((k, j) => (j === i ? { ...k, ...patch } : k)));
  return (
    <div className="grid gap-2" data-testid="kpi-editor">
      <div className="flex items-center justify-between"><div className={lbl}>KPI di crescita</div>
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...value, EMPTY_KPI])} className="rounded-full font-bold" data-testid="kpi-add"><Plus className="h-4 w-4 mr-1" />Aggiungi KPI</Button></div>
      <p className="text-xs text-slate-600">Sul sito pubblico compaiono solo i KPI confermati. Inserisci solo numeri reali e verificati.</p>
      {value.map((k, i) => (
        <div key={i} className={`rounded-2xl border p-3 grid gap-2 ${k.confermato ? "border-emerald-300 bg-emerald-50/60" : "border-amber-300 bg-amber-50"}`} data-testid={`kpi-row-${i}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            {k.confermato
              ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-bold text-white" data-testid={`kpi-confirmed-${i}`}><BadgeCheck className="h-3.5 w-3.5" />Confermato · visibile sul sito</span>
              : <span className="inline-flex items-center gap-1 rounded-full bg-amber-400 px-2.5 py-1 text-[11px] font-bold text-[#0A1128]" data-testid={`kpi-pending-${i}`}><AlertTriangle className="h-3.5 w-3.5" />Da confermare · non pubblicato</span>}
            <div className="flex gap-1">
              {k.confermato
                ? <Button type="button" size="sm" variant="outline" onClick={() => set(i, { confermato: false })} className="rounded-full text-xs font-bold" data-testid={`kpi-unconfirm-${i}`}>Rimetti in bozza</Button>
                : <Button type="button" size="sm" onClick={() => set(i, { confermato: true })} disabled={!k.valore || k.valore.trim() === "—"} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-xs font-bold" data-testid={`kpi-confirm-${i}`}><BadgeCheck className="h-4 w-4 mr-1" />Conferma dato</Button>}
              <Button type="button" size="sm" variant="ghost" onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label="Rimuovi KPI" data-testid={`kpi-remove-${i}`}><Trash2 className="h-4 w-4 text-[#E8173A]" /></Button>
            </div>
          </div>
          <div className="grid gap-2 md:grid-cols-[1fr_1fr_120px]">
            <Input value={k.label.it} onChange={(e) => set(i, { label: { ...k.label, it: e.target.value } })} placeholder="Etichetta IT (es. Follower Instagram)" className="bg-white rounded-xl" data-testid={`kpi-label-it-${i}`} />
            <Input value={k.label.en} onChange={(e) => set(i, { label: { ...k.label, en: e.target.value } })} placeholder="Label EN" className="bg-white rounded-xl" data-testid={`kpi-label-en-${i}`} />
            <Input value={k.valore} onChange={(e) => set(i, { valore: e.target.value })} placeholder="+180%" className="bg-white rounded-xl font-bold" data-testid={`kpi-valore-${i}`} />
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            <Input value={k.periodo.it} onChange={(e) => set(i, { periodo: { ...k.periodo, it: e.target.value } })} placeholder="Periodo IT (facoltativo, es. in 12 mesi)" className="bg-white rounded-xl" data-testid={`kpi-periodo-it-${i}`} />
            <Input value={k.periodo.en} onChange={(e) => set(i, { periodo: { ...k.periodo, en: e.target.value } })} placeholder="Period EN (optional)" className="bg-white rounded-xl" data-testid={`kpi-periodo-en-${i}`} />
          </div>
        </div>
      ))}
    </div>
  );
};

export const IG_RX = /^https?:\/\/(?:www\.|m\.)?instagram\.com\/(?:[A-Za-z0-9_.]+\/)?(p|reel|tv)\/([A-Za-z0-9_-]+)\/?(?:[?#].*)?$/;
export const normIg = (u) => { const m = IG_RX.exec((u || "").trim()); return m ? `https://www.instagram.com/${m[1]}/${m[2]}/` : null; };

export const InstagramPostsEditor = ({ value, onChange }) => {
  const [txt, setTxt] = useState("");
  const add = () => {
    const n = normIg(txt);
    if (!n) { toast.error("Link non valido: incolla un link a un post (instagram.com/p/…) o reel (instagram.com/reel/…)"); return; }
    if (value.includes(n)) { toast.error("Post già presente"); return; }
    if (value.length >= 6) { toast.error("Massimo 6 post"); return; }
    onChange([...value, n]); setTxt("");
  };
  const move = (i, d) => { const x = [...value]; [x[i], x[i + d]] = [x[i + d], x[i]]; onChange(x); };
  return (
    <div className="grid gap-2" data-testid="ig-posts-editor">
      <div className={lbl}>Post e reel Instagram (max 6)</div>
      <div className="flex gap-2">
        <Input value={txt} onChange={(e) => setTxt(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="https://www.instagram.com/p/… oppure /reel/…" className="bg-white rounded-xl" data-testid="ig-post-input" />
        <Button type="button" onClick={add} disabled={!txt.trim() || value.length >= 6} className="rounded-full bg-[#0A1128] font-bold" data-testid="ig-post-add"><Plus className="h-4 w-4 mr-1" />Aggiungi</Button>
      </div>
      {value.map((u, i) => (
        <div key={u} className="flex items-center gap-2 rounded-xl bg-white px-3 py-1.5 border border-[#5FB3EA]" data-testid={`ig-post-row-${i}`}>
          <Instagram className="h-4 w-4 text-[#E8173A] shrink-0" />
          <a href={u} target="_blank" rel="noreferrer" className="text-sm font-semibold truncate flex-1 min-w-0">{u.replace("https://www.instagram.com", "")}</a>
          <Button type="button" variant="ghost" size="sm" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Su" data-testid={`ig-post-up-${i}`}><ArrowUp className="h-4 w-4" /></Button>
          <Button type="button" variant="ghost" size="sm" disabled={i === value.length - 1} onClick={() => move(i, 1)} aria-label="Giù" data-testid={`ig-post-down-${i}`}><ArrowDown className="h-4 w-4" /></Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(value.filter((x) => x !== u))} aria-label="Rimuovi" data-testid={`ig-post-remove-${i}`}><X className="h-4 w-4" /></Button>
        </div>
      ))}
    </div>
  );
};

export const PublishSwitch = ({ checked, onChange, testid }) => (
  <label className="inline-flex items-center gap-2 text-sm font-bold text-[#0A1128]">
    <Switch checked={checked} onCheckedChange={onChange} className="data-[state=checked]:bg-[#E8173A] data-[state=unchecked]:bg-[#5FB3EA]" data-testid={testid} />
    {checked ? "Pubblicata" : "Bozza"}
  </label>
);
